import { useState } from 'react';

import { useAcceptTermsMutation } from '@/hooks/mutations/useAcceptTermsMutation';
import { dictionaries, useTranslation } from '@/i18n';

interface RequirementsModalProps {
  isOpen: boolean;
  onAccept: () => void;
  trackingNumber: string;
}

export const RequirementsModal = ({
  isOpen,
  onAccept,
  trackingNumber,
}: RequirementsModalProps) => {
  const [isChecked, setIsChecked] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { isLoading, mutate: acceptTerms } = useAcceptTermsMutation();
  const { locale, t } = useTranslation();
  const requirements = dictionaries[locale].requirements.items;

  if (!isOpen) return null;

  const handleAccept = () => {
    setErrorMessage(null);

    acceptTerms(
      { transportId: trackingNumber },
      {
        onError: (error) => {
          if (error.response?.status === 404) {
            onAccept();
            return;
          }

          setErrorMessage(
            error.response?.data?.error?.message ??
              t('requirements.acceptError')
          );
        },
        onSuccess: (response) => {
          if (response.success) {
            onAccept();
            return;
          }

          setErrorMessage(t('requirements.acceptError'));
        },
      }
    );
  };

  return (
    <div className='fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 px-4'>
      <div className='w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl'>
        <h2 className='mb-4 text-xl font-bold text-gray-900'>
          {t('requirements.title')}
        </h2>
        <p className='mb-4 text-sm text-gray-600'>{t('requirements.intro')}</p>

        <ul className='mb-6 space-y-3'>
          {requirements.map((requirement, index) => (
            <li key={index} className='flex items-start gap-3'>
              <span className='flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-greenSecondary text-xs font-semibold leading-none text-white'>
                {index + 1}
              </span>
              <span className='pt-px text-sm leading-5 text-gray-700'>
                {requirement}
              </span>
            </li>
          ))}
        </ul>

        <label className='mb-6 flex cursor-pointer items-center gap-3'>
          <input
            type='checkbox'
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            className='h-5 w-5 cursor-pointer rounded border-gray-300 text-greenSecondary focus:ring-greenSecondary'
          />
          <span className='text-sm font-medium text-gray-700'>
            {t('requirements.checkboxLabel')}
          </span>
        </label>

        {errorMessage && (
          <p className='mb-4 text-sm text-red-600'>{errorMessage}</p>
        )}

        <button
          onClick={handleAccept}
          disabled={!isChecked || isLoading}
          className={`w-full rounded-xl py-3 text-base font-bold transition-all ${
            isChecked && !isLoading
              ? 'bg-greenSecondary text-white hover:bg-opacity-90'
              : 'cursor-not-allowed bg-gray-200 text-gray-400'
          }`}
        >
          {isLoading
            ? t('requirements.accepting')
            : t('requirements.acceptAndContinue')}
        </button>
      </div>
    </div>
  );
};
